import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 } from 'uuid';
import {
  DialogHandle,
  SnackbarContext,
  SnackbarVariant,
  TableContext,
  TableFilterState,
  UserContext,
  useEventCallback,
} from '@eas/common-web';
import { CALENDAR_TABLE_VERSION } from '@modules/planner/work-order-table/work-order-table';
import { COLUMNS_KEYS_ORDER_SNAPHOT } from '@modules/work-order/work-order-columns';
import { NamedSettingsContext } from '../context/named-settings-context';
import { NamedSettings } from '../context/named-settings-types';
import prodBackupFilters from './prod-named-filters-backup.json';
import {
  NamedTableSettings,
  ParsedTableNamedSettings,
} from './table-named-settings-types';

export function useNamedSettingsMenu(tag: string) {
  const {
    getNamedSettings,
    saveNamedSettings,
    deleteNamedSettings,
    adminPermission,
  } = useContext(NamedSettingsContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const {
    filtersState,
    setFiltersState,
    columnsState,
    setColumnsState,
    sorts,
    setSorts,
    columns,
  } = useContext(TableContext);

  const intl = useIntl();

  const { user, hasPermission } = useContext(UserContext);
  const hasAdminPermission = adminPermission && hasPermission(adminPermission);

  const [opened, setOpened] = useState<boolean>(false);
  const [savedItems, setSavedItems] = useState<ParsedTableNamedSettings[]>([]);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const confirmDeleteDialog = useRef<DialogHandle>(null);
  const createDialog = useRef<DialogHandle>(null);

  const selectSaved = useEventCallback(
    (settings?: ParsedTableNamedSettings) => {
      if (!settings) {
        setSelectedItem(null);
        return;
      }
      console.log('selectSaved');
      setSelectedItem((saved) => {
        if (saved === settings.id) {
          // toto je deaktivacia filtru, klikol som totiz na ten isty setting ktory uz bol zvoleny
          setFiltersState((filtersState) => {
            return filtersState.map((state) => ({
              ...state,
              value: null,
              enabled: false,
              object: null,
              filters: [],
              selectFilter: [],
              numberFilter: null,
            }));
          });

          return null;
        }

        const parsed: NamedTableSettings = settings.settings;
        console.log({ filtersState });
        if (parsed.filtersState != null) {
          setFiltersState(parsed.filtersState);
        }

        if (parsed.columnsState != null) {
          // tuto to padalo, treba odfiltrovat vsetky columns ktore nenajdem v liste aktualnom
          const newColumnState = parsed.columnsState.filter((c) =>
            columns.some((current) => current.datakey === c.datakey)
          );
          console.log('newColumnState = ', newColumnState);
          setColumnsState(newColumnState);
        }

        if (parsed.sorts != null) {
          setSorts(parsed.sorts);
        }

        return settings.id;
      });
    }
  );

  const load = useCallback(async () => {
    const settings = await getNamedSettings(tag);
    settings.sort((a, b) => a.name.localeCompare(b.name));
    const parsed = settings.map((item) => {
      const p: NamedTableSettings = JSON.parse(item.settings);
      return {
        ...item,
        settings: p,
      };
    });
    console.log({ parsed });
    setSavedItems(parsed);
  }, [getNamedSettings, tag]);

  const createSaved = useEventCallback(
    async (
      settings: ParsedTableNamedSettings,
      keepOldSettings = false,
      initialTop = false
    ) => {
      const newTableSettings: NamedTableSettings = keepOldSettings
        ? settings.settings
        : {
            filtersState: filtersState
              .map((state, i) => ({
                ...state,
                columnDatakey: columns[i]?.datakey,
              }))
              .filter((f) => f.columnDatakey),

            columnsState,
            sorts,
            topOrder: 0,
            isTop: initialTop,
            version: CALENDAR_TABLE_VERSION,
          };

      const newSettings: NamedSettings = {
        ...settings,
        settings: JSON.stringify(newTableSettings),
        tag,
      };

      const result = await saveNamedSettings(newSettings);
      if (!keepOldSettings) {
        setSelectedItem(result.id);
      }

      const message = intl.formatMessage({
        id: 'EAS_TABLE_SAVED_SAVE_SUCCESS',
        defaultMessage: 'Nastavenie uložené',
      });

      showSnackbar(message, SnackbarVariant.SUCCESS);

      await load();
    }
  );

  const addVersioningToFilters = async () => {
    const newSettings: typeof savedItems = [];
    savedItems
      .filter((item) => {
        if (!hasAdminPermission) {
          return !item.shared;
        } else {
          return true;
        }
      })
      .forEach((item) => {
        const updatedFiltersState = item.settings.filtersState
          .map((f, i) => ({
            ...f,
            columnDatakey: COLUMNS_KEYS_ORDER_SNAPHOT?.[i],
          }))
          .filter((f) => f.columnDatakey);

        item.settings.filtersState = updatedFiltersState;
        item.settings.version = 1;
        item.settings.isTop = false;
        newSettings.push(item);
      });

    const delpromises = newSettings.map((s) => {
      const ns: NamedSettings = {
        ...s,
        settings: JSON.stringify(s.settings),
        tag,
      };
      return deleteNamedSettings(ns.id);
    });

    await Promise.all(delpromises);

    const promises = newSettings.map((s) => {
      const ns: NamedSettings = {
        ...s,
        settings: JSON.stringify(s.settings),
        tag,
      };
      return saveNamedSettings(ns);
    });
    await Promise.all(promises);
    showSnackbar('Nastavenia úspešne aktualizované.', SnackbarVariant.SUCCESS);
    await load();
  };

  const updateFiltersStateVersion = async (
    savedItems: ParsedTableNamedSettings[]
  ) => {
    const newSettings: typeof savedItems = [];

    savedItems
      .filter((item) => {
        if (!hasAdminPermission) {
          return !item.shared;
        } else {
          return true;
        }
      })
      .forEach((item) => {
        const updatedFiltersState: typeof item.settings.filtersState = [];
        columns.forEach((c) => {
          const filterIndex = item.settings.filtersState.findIndex(
            (f) => f.columnDatakey === c.datakey
          );
          if (filterIndex !== -1) {
            updatedFiltersState.push(item.settings.filtersState[filterIndex]);
          } else {
            // toto znamena ze bol novy column pridany niekam doprostred, myslim
            console.log('NOT FOUND', c.datakey);
            updatedFiltersState.push({
              value: null,
              enabled: false,
              object: null,
              filters: [],
              selectFilter: [],
              numberFilter: null,
              datakey: c.datakey,
            } as unknown as TableFilterState);
          }
        });
        item.settings.filtersState = updatedFiltersState;
        item.settings.columnsState = item.settings.columnsState.filter((cs) =>
          columns.some((c) => c.datakey === cs.datakey)
        );
        item.settings.version = CALENDAR_TABLE_VERSION;
        newSettings.push(item);
      });

    const delpromises = newSettings.map((s) => {
      const ns: NamedSettings = {
        ...s,
        settings: JSON.stringify(s.settings),
        tag,
      };
      return deleteNamedSettings(ns.id);
    });

    await Promise.all(delpromises);

    const promises = newSettings.map((s) => {
      const ns: NamedSettings = {
        ...s,
        settings: JSON.stringify(s.settings),
        tag,
      };
      return saveNamedSettings(ns);
    });
    await Promise.all(promises);
    showSnackbar('Nastavenia úspešne aktualizované.', SnackbarVariant.SUCCESS);
    await load();
  };

  const importOldSettings = async () => {
    console.log('Adding versioning ...');
    console.log('Finished: ');
    console.log({ savedItems });
    await addVersioningToFilters();
  };

  const updateSettingsVersion = async () => {
    console.log('Updating filters version ...');
    await updateFiltersStateVersion(savedItems);
  };

  const updateSaved = useEventCallback(async () => {
    const selectedSettings = savedItems.find(
      (item) => item.id === selectedItem
    );

    if (selectedItem && selectedSettings) {
      await deleteNamedSettings(selectedItem);

      await createSaved(
        selectedSettings,
        false,
        selectedSettings?.settings?.isTop
      );

      const message = intl.formatMessage({
        id: 'EAS_TABLE_SAVED_UPDATE_SUCCESS',
        defaultMessage: 'Nastavení uloženo',
      });

      showSnackbar(message, SnackbarVariant.SUCCESS);
    }
  });

  const updateSavedIsTop = useEventCallback(
    async (id: string, isTop?: boolean) => {
      const selectedSettings = savedItems.find((item) => item.id === id);

      if (selectedSettings) {
        await deleteNamedSettings(id);

        await createSaved(
          {
            ...selectedSettings,
            settings: { ...selectedSettings.settings, isTop: isTop ?? false },
          },
          true
        );

        const message = intl.formatMessage({
          id: 'EAS_TABLE_SAVED_UPDATE_SUCCESS',
          defaultMessage: 'Nastavení uloženo',
        });

        showSnackbar(message, SnackbarVariant.SUCCESS);
      }
    }
  );

  const deleteSaved = useEventCallback(async (id: string) => {
    await deleteNamedSettings(id);
    setSelectedItem(null);

    const message = intl.formatMessage({
      id: 'EAS_TABLE_SAVED_DELETE_SUCCESS',
      defaultMessage: 'Nastavení odstraněno',
    });

    showSnackbar(message, SnackbarVariant.SUCCESS);

    await load();
  });

  const openMenu = useCallback(() => {
    setOpened(true);
  }, []);

  const closeMenu = useCallback(() => {
    setOpened(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = savedItems.find((item) => item.id === selectedItem);
  const isAuthor = selected?.user?.id === user?.id;

  const canEdit = isAuthor;
  /**
   * temp test fn, do not use in PROD env
   */
  const importProdNamedFiltersFromBackup = async () => {
    await Promise.all(savedItems.map((item) => deleteNamedSettings(item.id)));

    const newFilters = prodBackupFilters.map((f) => ({
      tag,
      shared: f.shared,
      name: f.name,
      settings: f.settings,
      id: v4(),
    }));

    await Promise.all(newFilters.map((f) => saveNamedSettings(f)));
    window.location.reload();
  };

  return {
    opened,
    openMenu,
    closeMenu,
    savedItems,
    selectSaved,
    createSaved,
    updateSaved,
    deleteSaved,
    selectedItem,
    confirmDeleteDialog,
    createDialog,
    canEdit,
    updateSavedIsTop,
    importOldSettings,
    updateSettingsVersion,
    importProdNamedFiltersFromBackup,
  };
}
